import React, { lazy, useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import { getOrder, ordersState } from "@/store/features/orderSlice"
import { getProductsById } from "@/components/cart/cartHelper"
import { getAttributeValue, getShortenedUrl, getLangCode } from "@/utils/helper"
import { validateRecaptcha } from "@/components/core/ReCaptcha/ReCaptcha"
import { checkoutState, getProfile } from "@/store/features/checkoutSlice"
import { getConfig } from "@/constants/config"
import { showToast, selectGenericState } from "@/store/features/genericSlice"
import useRegisterOnlineProducti18n from "@/i18n/useRegisterOnlineProducti18n"
import { getLocations } from "@/components/RegisterOfflineProduct/v1/RegisterOfflineHelper"
import PageLoader from "@/components/core/PageLoader/PageLoader"
import {
  personalInfoEdit,
  selectProdcutEdit,
  addReviewRegistrationAnalytics,
  addReviewRegistrationEventInfo,
} from "@/components/RegisterOnlineProduct/v1/RegisterOnlineProductAnalytics"
import {
  getDDMMYYYYDate,
  submitRegistrationGenericError,
} from "@/components/RegisterOnlineProduct/v1/RegisterOnlineHelper"

const RegisterOnlineProductView = lazy(() =>
  import("@/components/RegisterOnlineProduct/v1/RegisterOnlineProductView")
)
const SelectProducts = lazy(() =>
  import("@/components/RegisterOnlineProduct/v1/SelectProducts")
)
const RegistrationInformation = lazy(() =>
  import("@/components/RegisterOnlineProduct/v1/RegistrationInformation")
)
const ReviewRegistration = lazy(() =>
  import("@/components/RegisterOnlineProduct/v1/ReviewRegistration")
)
import { selectAuthState } from "@/store/features/authSlice"
import {
  getProductsForRegistration,
  onlineProductSubmitRegistration,
  setOnlineSubmitStatus,
  selectMyAccountState,
} from "@/store/features/myProductSlice"
import {
  API_FAILURE_STATUS,
  API_SUCCESS_STATUS,
  annSacksConstants,
} from "@/constants/index"

import MissingImage from "@/public/images/image-missing.png"
import useSearchi18n from "@/i18n/useSearchi18n"

const RegisterOnlineProduct = props => {
  const dispatch = useDispatch()
  const { isAuth, user = {} } = useSelector(selectAuthState)
  const { profileInfo } = useSelector(checkoutState)
  const { countryList: countryLists, stateToCountryList: countries } =
    useSelector(selectGenericState)
  const { status = "loading" } = useSelector(selectMyAccountState)
  const { status: orderNumberStatus } = useSelector(ordersState)
  const { status: noOrderNumberStatus } = useSelector(selectMyAccountState)

  const { data: componentProps } = props

  const staticText = useRegisterOnlineProducti18n({
    componentProps,
  })

  const {
    genericError = "",
    serverError = "",
    selectProducts = "",
    registerInfo = "",
    review = "",
  } = staticText

  const [isSuccess, setIsSuccess] = useState(false)
  const [todayDate, setTodayDate] = useState("")
  const [customerDetails, setCustomerDetails] = useState({})
  const [productDetails, setProductDetails] = useState([])
  const [country, setCountry] = useState([])
  const [stateList, setStateList] = useState([])
  const [general, setGeneral] = useState({})
  const [productData, setProductData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [data, setData] = useState([])
  const [isloading, setIsLoading] = useState(true)
  const [langCode, setLangCode] = useState(process.env.NEXT_PUBLIC_LOCALE)
  const [siteName, setSiteName] = useState(null)
  const { brand } = useSearchi18n({})

  useEffect(() => {
    getConfigs()
  }, [])

  useEffect(() => {
    if (isAuth && user) {
      dispatch(getProfile())
      getLocations()
    }
  }, [isAuth, user])

  useEffect(() => {
    if (!_isEmpty(general)) {
      fetchTodayDate()
      fetchIdFromUrl()
    }
  }, [general])

  useEffect(() => {
    if (countries?.length) {
      const country =
        countryLists.find(country => country.value === general?.country)
          ?.text ?? ""
      const stateList =
        countries.find(country => country.code === "US")?.states ?? []
      setCountry(country)
      setStateList(stateList)
    }
  }, [countries])

  useEffect(() => {
    if (
      (orderNumberStatus === API_SUCCESS_STATUS && data?.length > 0) ||
      (orderNumberStatus === API_FAILURE_STATUS && data?.length === 0) ||
      (noOrderNumberStatus === API_SUCCESS_STATUS && data?.length > 0) ||
      (noOrderNumberStatus === API_FAILURE_STATUS && data?.length === 0)
    ) {
      setIsLoading(false)
    }
  }, [orderNumberStatus, noOrderNumberStatus, data])

  useEffect(() => {
    if (productData.length && filteredData.length && !_isEmpty(general)) {
      updateProductDetails()
    }
  }, [productData, filteredData, general])

  useEffect(() => {
    if (user && profileInfo) {
      getAddresses()
    }
  }, [profileInfo, user])

  const getAddresses = () => {
    const prefilledCustomerDetails = {
      firstName: { value: user?.firstName },
      lastName: { value: user?.lastName },
    }

    if (profileInfo?.addresses?.length) {
      const defaultAddress = profileInfo?.addresses.find(
        item => item.id === profileInfo.defaultShippingAddressId
      )
      if (!_isEmpty(defaultAddress)) {
        const { city, phone, postalCode, state, streetName } = defaultAddress
        prefilledCustomerDetails["city"] = { value: city }
        prefilledCustomerDetails["phone"] = { value: phone }
        prefilledCustomerDetails["address"] = { value: streetName }
        prefilledCustomerDetails["zipCode"] = { value: postalCode }
        prefilledCustomerDetails["state"] = { value: state }
      }
    }
    setCustomerDetails(prefilledCustomerDetails)
  }

  const fetchTodayDate = () => {
    const today = new Date()
    const todayDate = getDDMMYYYYDate(today)
    setTodayDate(todayDate)
  }

  const getConfigs = () => {
    getConfig().then(config => {
      const locale = _get(config, "internationalization.locale", "")
      const country = _get(config, "internationalization.country", "")
      const legalPageLinks = _get(config, "legalPageData.map", {})
      const continueShopPath = _get(config, "general.continueShopPath", "")
      const langCode = getLangCode(config)
      const siteName = config?.general?.siteName
      const general = {
        locale,
        country,
        legalPageLinks,
        continueShopPath,
      }
      setSiteName(siteName)
      setLangCode(langCode)
      setCountry(general?.country)
      getShortenedUrl(_get(config, "general.myProductsPath", ""))
        .then(myProductsPath =>
          setGeneral({ ...general, myProductsPath: myProductsPath })
        )
        .catch(() => setGeneral(general))
    })
  }

  const fetchIdFromUrl = async () => {
    let data = []
    const urlParams = new URLSearchParams(window.location.search)
    const orderNum = urlParams.get("orderNumber")
    if (orderNum) {
      data = await dispatch(getOrder({ orderNo: orderNum, isAuth })).unwrap()
      if (!_isEmpty(data)) {
        await fetchOrderDetails(data)
      }
    } else {
      await fetchProducts()
    }
  }

  const checkAsProduct = itemBrand => {
    return _get(itemBrand, "value", "")?.toLowerCase() ===
      annSacksConstants?.asBrandName
      ? true
      : false
  }

  const fetchOrderDetails = async (data = []) => {
    const productsDetails = []
    // When user clicks on the register product link from past order details pages
    const lineItems = _get(data[0], "lineItems", [])
    if (lineItems?.length) {
      lineItems.forEach(item => {
        const orderDate = new Date(_get(data[0], "createdAt", ""))
        const attributes = _get(item?.variant, "attributes", [])
        const customerBrandName = attributes?.find(
          obj => obj?.name === "CustomerFacingBrand"
        )
        const customerColorValue = attributes?.find(
          obj => obj?.name === "SKUColorFinishName"
        )
        const customerDescription = attributes?.find(
          obj => obj?.name === "ProductDescriptionProductShort"
        )
        const customerCategory = attributes?.find(
          obj => obj?.name === "ProductLocalCategory"
        )
        // Get register order details
        !checkAsProduct(customerBrandName) &&
          productsDetails.push({
            orderNumber: _get(data[0], "orderNumber", ""),
            purchased: `${getDDMMYYYYDate(orderDate)}`,
            productName: _get(item, `name[${general?.locale}]`, ""),
            skuid: _get(item, "variant.sku", ""),
            brandName: _get(customerBrandName, "value", ""),
            colorValue: _get(
              customerColorValue,
              `value[${general?.locale}]`,
              ""
            ),
            description: _get(
              customerDescription,
              `value[${general?.locale}]`,
              ""
            ),
            imgSrc:
              item?.variant?.images?.length > 0
                ? _get(item?.variant, "images[0].url", "")
                : MissingImage,
            customerFacingSKU: getAttributeValue(
              attributes,
              "CustomerFacingSKU"
            ),
            slug: _get(item, `productSlug[${general?.locale}]`, ""),
            category: _get(customerCategory, "value", ""),
          })
      })
    }
    setData(productsDetails)
    setIsLoading(false)
  }

  // When user clicks on the register product link from my orders page.
  const updateProductDetails = () => {
    const productsDetails = []
    filteredData?.forEach(item => {
      const product = productData?.find(prod => prod?.id === item?.productId)
      // Get product variant
      let productVariant = {}
      if (item?.variantId === product?.masterVariant?.id) {
        productVariant = product?.masterVariant ?? {}
      } else {
        productVariant =
          product?.variants?.find(variant => variant?.id === item?.variantId) ??
          {}
      }
      if (!_isEmpty(productVariant)) {
        const orderDate = new Date(
          _get(item?.custom?.fields, "lastPurchasedOn", "")
        )
        const attributes = _get(productVariant, "attributes", [])
        const customerBrandName = attributes?.find(
          obj => obj?.name === "CustomerFacingBrand"
        )
        const customerColorValue = attributes?.find(
          obj => obj?.name === "SKUColorFinishName"
        )
        const customerDescription = attributes?.find(
          obj => obj?.name === "ProductDescriptionProductShort"
        )
        const customerFacingSKU = attributes?.find(
          obj => obj?.name === "CustomerFacingSKU"
        )
        const customerCategory = attributes?.find(
          obj => obj?.name === "ProductLocalCategory"
        )
        // Get register order details
        !checkAsProduct(customerBrandName) &&
          productsDetails.push({
            lineItemId: _get(item, "id", ""),
            orderNumber: _get(item?.custom?.fields, "orderNumber", ""),
            purchased: `${getDDMMYYYYDate(orderDate)}`,
            productName: _get(item, `name[${general?.locale}]`, ""),
            skuid: _get(productVariant, "sku", ""),
            brandName: _get(customerBrandName, "value", ""),
            colorValue: _get(
              customerColorValue,
              `value[${general?.locale}]`,
              ""
            ),
            description: _get(
              customerDescription,
              `value[${general?.locale}]`,
              ""
            ),
            imgSrc: _get(productVariant, "images[0].url", ""),
            customerFacingSKU: _get(customerFacingSKU, "value", ""),
            slug: _get(product, `slug[${general?.locale}]`, ""),
            category: _get(customerCategory, "value", ""),
          })
      }
    })
    setData(productsDetails)
  }

  const fetchProducts = async () => {
    let filteredData = []
    await dispatch(getProductsForRegistration())
      .unwrap()
      .then(async res => {
        if (res[0] && res[1]) {
          const myProductslineItems =
            res[0].results[0]?.lineItems?.filter(
              item => item?.custom?.fields?.isBundleComponent === false
            ) ?? []
          const lineItemsRegistered = res[1].response ?? []
          const productArr = []
          myProductslineItems.forEach(item => {
            const filteredProductId = lineItemsRegistered
              .map(list => list.products)
              .flat()
              .some(listData => listData.ctLineItemId === item.id)
            if (!filteredProductId) {
              productArr.push(item)
            }
          })
          filteredData = productArr
          const productData = await getProductsById(
            productArr.map(item => item.productId)
          )
          setProductData(productData)
        }
      })
    setFilteredData(filteredData)
    setIsLoading(false)
  }

  const getProductDetails = productData => {
    setProductDetails(productData)
  }

  const submitRegistration = () => {
    const selectedState = customerDetails?.state?.value ?? ""

    const getProductsDetails = []
    if (productDetails && productDetails.length > 0) {
      productDetails.forEach(item => {
        const productList = {}
        productList.sku = _get(item, "skuid", "")
        productList.brand = _get(item, "brandName", "")
        productList.purchasedate = _get(item, "purchased", "")
        productList.ctLineItemId = _get(item, "lineItemId", "")
        productList.placeOfPurchase = "Internet"
        getProductsDetails.push(productList)
      })
    }
    const body = {
      customer: {
        email: user.sub,
        firstName: _get(customerDetails, "firstName.value", ""),
        lastName: _get(customerDetails, "lastName.value", ""),
        addressLine1: _get(customerDetails, "address.value", ""),
        city: _get(customerDetails, "city.value", ""),
        state: selectedState,
        zipCode: _get(customerDetails, "zipCode.value", ""),
        country: "US",
        ...(siteName !== brand ? { LangCode: langCode } : {}),
        ...(customerDetails.installationDate.value
          ? { installationDate: customerDetails.installationDate.value }
          : {}),
        phone: _get(customerDetails, "phone.value", ""),
      },
      products: getProductsDetails,
    }

    validateRecaptcha().then(async res => {
      if (res.success) {
        dispatch(onlineProductSubmitRegistration(body))
          .unwrap()
          .then(payload => {
            setIsSuccess(payload)
            payload
              ? addReviewRegistrationAnalytics(
                  "cmp:click",
                  "success",
                  productDetails,
                  ""
                )
              : submitRegistrationGenericError(productDetails, genericError)
          })
          .catch(err => {
            submitRegistrationGenericError(productDetails, genericError)
            dispatch(
              setOnlineSubmitStatus({
                submitError: serverError,
                isDisabled: false,
              })
            )
            console.error(err)
          })
      } else {
        dispatch(
          showToast({
            message: genericError,
            isVisible: true,
          })
        )
      }
    })
  }

  const steps = [
    {
      label: selectProducts,
      editButtonAnalytics: selectProdcutEdit,
      content: SelectProducts,
      props: {
        staticText: staticText,
        data: data,
        getProductDetails,
        productDetails: productDetails,
        title: selectProducts,
      },
    },
    {
      label: registerInfo,
      editButtonAnalytics: personalInfoEdit,
      content: RegistrationInformation,
      props: {
        staticText: staticText,
        stateData: stateList,
        productData: productDetails,
        getCustomerDetails: setCustomerDetails,
        countries: countries,
        country: country,
        data: customerDetails,
        title: registerInfo,
      },
    },
    {
      label: review,
      content: ReviewRegistration,
      props: {
        staticText: staticText,
        submitRegistration: submitRegistration,
        legalPages: general?.legalPageLinks,
        addReviewRegistrationEventInfo,
      },
    },
  ]

  return (
    <>
      <PageLoader loading={status === "loading"} />
      {steps.length > 0 && (
        <RegisterOnlineProductView
          i18n={staticText}
          steps={steps}
          data={data}
          productDetails={productDetails}
          customerDetails={customerDetails}
          isSuccess={isSuccess}
          myProductsPath={general?.myProductsPath}
          todayDate={todayDate}
          continueShoppingPath={general?.continueShopPath}
          isAuth={isAuth}
          isloading={isloading}
        />
      )}
    </>
  )
}
export default RegisterOnlineProduct
